import { Sparkles, ChevronRight, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import InfoSheet from "../common/InfoSheet";
import type { HomePageData } from "../../services/HomeService";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import {
  buildHealthInsightMetricCards,
  formatHealthInsightScore,
  healthInsightBadgeClass,
} from "../../utils/healthInsightsMetrics";
import { gluceraBrandSolidBlueButton, gluceraHomeEyebrow, gluceraHomeStatusBadge } from "../../utils/gluceraTypography";
import { cn } from "../../lib/utils";
import { homeAccentLink, homeAccentText, homeAccentChip } from "../../utils/homeBlueTheme";

interface Props {
  healthInsights: NonNullable<HomePageData["Health Insights"]>;
  onContinue?: () => void;
}

const COLLAPSED_COUNT = 2;

const HealthInsightsHomeCard = ({ healthInsights, onContinue }: Props) => {
  const navigate = useNavigate();
  const metrics = buildHealthInsightMetricCards(healthInsights);
  // Collapsed by default to keep the home page short; user expands with "See more".
  const [expanded, setExpanded] = useState(false);
  const canCollapse = metrics.length > COLLAPSED_COUNT;
  const visibleMetrics =
    expanded || !canCollapse ? metrics : metrics.slice(0, COLLAPSED_COUNT);

  return (
    <Card className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm">
      <div className="flex items-center justify-between border-b border-border/40 px-4 py-3">
        <div className="flex items-center gap-2">
          <Sparkles className={cn("h-4 w-4", homeAccentText)} />
          <span className="text-sm font-bold tracking-tight text-foreground">
            Health Insights
          </span>
          <span className={cn("rounded-full px-2.5 py-1", homeAccentChip, gluceraHomeStatusBadge)}>
            Today
          </span>
          <InfoSheet title="Health Insights" ariaLabel="About Health Insights">
            <div className="space-y-3">
              <p>
                Health Insights score four key parts of your daily nutrition from
                the meals you log, each rated 0–100:
              </p>
              <div className="space-y-1.5 text-xs">
                <p>
                  <span className="font-semibold text-foreground">Carb-Fiber Ratio</span> — how
                  balanced your carbs are with fiber.
                </p>
                <p>
                  <span className="font-semibold text-foreground">Fat Quality</span> — share of
                  healthy fats in your meals.
                </p>
                <p>
                  <span className="font-semibold text-foreground">Sugar Load</span> — how much
                  added/total sugar you ate.
                </p>
                <p>
                  <span className="font-semibold text-foreground">Protein-Fiber</span> — protein
                  and fiber that keep you full and steady.
                </p>
              </div>
              <div className="space-y-1.5">
                <p className="font-bold text-foreground">What the labels mean:</p>
                <div className="space-y-1 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 shrink-0 rounded-full bg-[#737DFE]" />
                    Excellent / Good — you&apos;re on track.
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 shrink-0 rounded-full bg-[#6268e8]" />
                    Needs improvement — small tweaks will help.
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 shrink-0 rounded-full bg-[#5258d4]" />
                    Poor — worth focusing on next meal.
                  </div>
                </div>
              </div>
              <p>Tap “View all” for the full breakdown and tips.</p>
            </div>
          </InfoSheet>
        </div>
        <button
          type="button"
          onClick={() => navigate("/glucera" + GluceraNavigationRoutes.HEALTH_INSIGHTS)}
          className={cn("flex items-center gap-0.5 text-xs font-semibold", homeAccentLink)}
        >
          View all <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2 p-3">
        {visibleMetrics.map((m) => {
          const scoreText = formatHealthInsightScore(m.score) ?? "—";
          return (
            <div
              key={m.label}
              className="flex flex-col gap-1 rounded-xl border border-border/50 bg-muted/30 p-3"
            >
              <span className={cn(gluceraHomeEyebrow, "text-foreground/80")}>
                {m.label}
              </span>
              <span className="text-xl font-black tabular-nums text-foreground">
                {scoreText}
              </span>
              {m.badge ? (
                <span
                  className={cn("w-fit rounded-full px-2.5 py-1", gluceraHomeStatusBadge, healthInsightBadgeClass(m.badge))}
                >
                  {m.badge}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
      {canCollapse ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className={cn("flex w-full items-center justify-center gap-1 border-t border-border/40 py-2.5 text-xs font-semibold transition-colors hover:bg-muted/40", homeAccentLink)}
        >
          {expanded ? "See less" : `See more (${metrics.length - COLLAPSED_COUNT})`}
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`}
          />
        </button>
      ) : null}
      {onContinue ? (
        <div className="border-t border-border/40 px-3 pb-3 pt-1">
          <Button
            type="button"
            onClick={onContinue}
            className={cn("mt-2 h-11 w-full rounded-xl", gluceraBrandSolidBlueButton)}
          >
            Back to home
          </Button>
        </div>
      ) : null}
    </Card>
  );
};

export default HealthInsightsHomeCard;
